import { useState } from 'react';
import { Icon } from './Icon';
import type { PreviewOutput } from '@/api/imports';
import { formatDateTime } from '@/lib/format';

/**
 * Aperçu : ce que les lignes du fichier VONT DEVENIR.
 *
 * Un décompte — « 50/50 lignes simulées sans rejet » — dit qu'aucune règle n'a
 * été violée. Il ne dit pas si le bon numéro a été pris pour pivot, si la date
 * retenue est la bonne, ni ce qui atterrira dans la fiche. C'est pourtant la
 * seule chose qu'un opérateur puisse réellement vérifier avant d'écrire dans un
 * journal où rien ne s'efface.
 *
 * Chaque ligne montre donc l'événement produit — commerçant, date, type — et,
 * dépliable, le contenu complet ainsi que la ligne source dont il provient.
 */
export function PreviewRows({ preview }: { preview: PreviewOutput }) {
  const [open, setOpen] = useState<number | null>(null);
  const rows = preview.rows ?? [];
  const shown = rows.slice(0, 8);

  return (
    <div className="preview-rows">
      <div className="preview-rows-head">
        <Icon name="activity" size={14} />
        Ce que deviendront les {shown.length} premières lignes
        <span className="muted">
          {preview.accepted}/{preview.sampleSize} sans rejet
          {preview.rejected > 0 ? ` · ${preview.rejected} rejet(s)` : ''}
        </span>
      </div>

      {shown.map((r) => (
        <div key={r.rowIndex} className={r.accepted ? 'preview-line' : 'preview-line ko'}>
          <button type="button" onClick={() => setOpen(open === r.rowIndex ? null : r.rowIndex)}>
            <span className="pl-state">{r.accepted ? '✓' : '✕'}</span>
            <span className="pl-phone">{r.subjectPhone || '— pivot manquant —'}</span>
            <span className="pl-date">{r.occurredAt ? formatDateTime(r.occurredAt) : '— date manquante —'}</span>
            <span className="pl-type">{r.attributeType}</span>
            <span className="pl-more">{open === r.rowIndex ? 'masquer' : 'détail'}</span>
          </button>

          {!r.accepted && r.message && <div className="pl-error">{r.code} — {r.message}</div>}

          {open === r.rowIndex && (
            <div className="pl-detail">
              <div>
                <span className="pl-detail-label">Contenu produit</span>
                <pre>{formatPayload(r.payload)}</pre>
              </div>
              <div>
                <span className="pl-detail-label">Ligne du fichier</span>
                <pre>{(r.raw ?? []).join('  |  ')}</pre>
              </div>
            </div>
          )}
        </div>
      ))}

      {rows.length > shown.length && (
        <div className="muted" style={{ fontSize: 12, fontWeight: 600, padding: '6px 2px' }}>
          … et {rows.length - shown.length} autres lignes simulées.
        </div>
      )}
    </div>
  );
}

function formatPayload(payload: unknown): string {
  if (payload == null) return '—';
  try {
    return JSON.stringify(payload, null, 2);
  } catch {
    return String(payload);
  }
}
